import { registerTier2Config } from "../tier2ConfigRegistry";
import { PGD_IDS } from "../../pgds/pgdIds";

// Rifaximin is a TREATMENT PGD — not preventive supply.
// The patient currently has travellers' diarrhoea.
// Red-flag screening is live at the point of supply
// (supplyGates), not prefilled from the intake record.
// The fitAndWell step in the runner handles the
// "not feverish" check; red-flag specifics are
// captured in supplyGates below.

registerTier2Config({
  pgdId: PGD_IDS.RIFAXIMIN,
  displayName: "Rifaximin (Xifaxanta\u00ae)",
  serviceType: "supply",
  minimumAgeYears: 18,
  indicationSummary:
    "Treatment of travellers\u2019 diarrhoea in adults aged 18 and over, where the diarrhoea is not associated with any red-flag features. This is a treatment PGD \u2014 the patient currently has travellers\u2019 diarrhoea. Do not supply if any red-flag feature is present.",

  brands: [
    {
      brandId: "rifaximin-200mg",
      brandName: "Rifaximin 200 mg (Xifaxanta\u00ae)",
      minimumAgeYears: 18,
      route: "supply",
      doseDescription: "200 mg (one tablet) every 8 hours for 3 days \u2014 9 tablets total",
      administrationNotes:
        "Supply 9 tablets only (one course). Label: one tablet every 8 hours for 3 days. Do not supply a second course \u2014 if symptoms persist beyond 3 days or recur, the patient must be referred to their GP. Urine may turn reddish in colour \u2014 this is a harmless rifamycin class effect; advise the patient in advance.",
    },
  ],

  schedules: [
    {
      scheduleId: "rifaximin-3-day",
      label: "200 mg every 8 hours for 3 days (9 tablets total) \u2014 single course only",
      offLabel: false,
      doses: [
        {
          doseNumber: 1,
          label: "Day 1 \u2014 supply today for self-administration",
        },
      ],
      completionNotes:
        "One course of 9 tablets only. Do not supply a second course. If symptoms persist beyond 3 days, if symptoms recur after completing the course, or if the patient develops any red-flag feature at any point, the patient must be referred to their GP or seek urgent medical attention.",
    },
  ],

  capturesPriorDoseHistory: false,

  supplyGates: [
    "RED FLAG SCREEN \u2014 confirm ALL of the following before proceeding. If any red flag is present, do NOT supply rifaximin \u2014 refer to a doctor immediately.",
    "No fever is present (temperature normal).",
    "No bloody diarrhoea \u2014 stools are watery or loose, not visibly blood-stained.",
    "Fewer than 8 unformed stools in the last 24 hours.",
    "No occult blood or leucocytes in the stool (if tested).",
    "No signs of significant dehydration requiring urgent medical attention.",
  ],

  exclusions: [
    {
      checkId: "rifaximin-ex-age",
      question: "Is the patient under 18 years of age?",
      disqualifyingAnswer: "yes",
      disqualificationMessage:
        "Rifaximin supply under this PGD is restricted to patients aged 18 and over. Do not supply.",
      referralMessage: "Refer to the patient\u2019s GP or an urgent care setting.",
    },
    {
      checkId: "rifaximin-ex-pregnancy-breastfeeding",
      question: "Is the patient known or suspected to be pregnant, or breastfeeding?",
      disqualifyingAnswer: "yes",
      disqualificationMessage: "Rifaximin is contraindicated in pregnancy and breastfeeding. Do not supply.",
      referralMessage: "Refer to the patient\u2019s GP or obstetrician for management of travellers\u2019 diarrhoea.",
    },
    {
      checkId: "rifaximin-ex-hypersensitivity",
      question:
        "Does the patient have known hypersensitivity to rifaximin, any other rifamycin antibiotic (including rifampicin or rifabutin), or any excipient of the product?",
      disqualifyingAnswer: "yes",
      disqualificationMessage:
        "Rifaximin is contraindicated in patients with hypersensitivity to rifaximin, other rifamycins, or any excipient. Do not supply.",
      referralMessage: "Refer to the patient\u2019s GP for management of travellers\u2019 diarrhoea.",
    },
    {
      checkId: "rifaximin-ex-obstruction",
      question: "Is intestinal obstruction known or suspected?",
      disqualifyingAnswer: "yes",
      disqualificationMessage: "Known or suspected intestinal obstruction is a contraindication to rifaximin. Do not supply.",
      referralMessage: "Refer to an emergency department or urgent care setting immediately.",
    },
    {
      checkId: "rifaximin-ex-renal-hepatic",
      question: "Does the patient have renal impairment or severe hepatic impairment?",
      disqualifyingAnswer: "yes",
      disqualificationMessage:
        "Renal impairment and severe hepatic impairment are each a contraindication to rifaximin under this PGD. Do not supply.",
      referralMessage: "Refer to the patient\u2019s GP for management of travellers\u2019 diarrhoea.",
    },
    {
      checkId: "rifaximin-ex-ciclosporin",
      question: "Is the patient taking ciclosporin?",
      disqualifyingAnswer: "yes",
      disqualificationMessage:
        "Ciclosporin markedly increases rifaximin exposure and co-administration is contraindicated. Do not supply.",
      referralMessage: "Refer to the patient\u2019s GP for management of travellers\u2019 diarrhoea.",
    },
  ],

  cautions: [
    {
      checkId: "rifaximin-caut-warfarin",
      question: "Is the patient taking warfarin or another oral anticoagulant?",
      cautionMessage:
        "Rifaximin may affect the INR in patients taking warfarin or other oral anticoagulants. Advise the patient to monitor for signs of bleeding and to have their INR checked. Notify the patient\u2019s GP of the supply.",
    },
    {
      checkId: "rifaximin-caut-liver-disease",
      question: "Does the patient have any known liver disease (even if not severe hepatic impairment)?",
      cautionMessage:
        "Patients with known liver disease are at higher risk of serious skin reactions with rifaximin. For Child-Pugh B or C hepatic impairment, do not supply \u2014 refer to the GP. For milder liver disease, exercise caution and document the clinical decision.",
    },
    {
      checkId: "rifaximin-caut-ibd",
      question: "Does the patient have a known diagnosis of inflammatory bowel disease (IBD)?",
      cautionMessage:
        "An IBD flare can mimic infectious travellers\u2019 diarrhoea. Refer known IBD patients for assessment before supplying an antibiotic for presumed infectious diarrhoea.",
    },
    {
      checkId: "rifaximin-caut-red-urine",
      question: "Has the pharmacist advised the patient that their urine may turn reddish in colour?",
      cautionMessage:
        "Rifaximin and other rifamycin-class medicines can cause reddish discolouration of the urine. This is harmless but may be alarming if the patient is not warned in advance. Confirm this advice has been given.",
    },
  ],

  requiresICVP: false,
  requiresLariamCard: false,
  requiresYellowCardDiscussion: false,
  requiresCHISNotificationUnder19: false,
  requiresGPNotification: true,
  standardObservationMinutes: 0,
  extendedObservationMinutes: 0,

  patientAdvicePoints: [
    "Take one tablet every 8 hours for 3 days (9 tablets in total). Try to take each dose at the same time each day.",
    "Rifaximin works in the gut \u2014 it is very poorly absorbed into the bloodstream, so it works locally where the infection is.",
    "Your urine may turn a reddish colour while you are taking rifaximin. This is harmless and is a normal effect of this class of medicine.",
    "Do NOT take a second course of rifaximin even if your symptoms return. If your diarrhoea has not improved after 3 days, or if your symptoms come back after you have finished the course, see a doctor.",
    "Seek urgent medical attention immediately if you develop any of the following: fever, blood in your stools, signs of severe dehydration (feeling very dizzy, not passing urine, very dry mouth), or if your symptoms are getting rapidly worse.",
    "Maintain your fluid intake throughout. Use oral rehydration sachets (available from a pharmacy) to replace lost salts and fluids. Loperamide may be used alongside rifaximin for symptom relief \u2014 ask the pharmacist.",
    "When you return home, if you have had travellers\u2019 diarrhoea during your trip, let your GP know, particularly if you feel unwell after you return.",
  ],
});
